import React, { useState, useRef } from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
  getIdToken
} from 'firebase/auth';
import firebaseApp from '../../Firebase';
import useCreateNewUser from '../../hooks/useCreateNewUser';
import { useLogin } from '../../hooks/useLogin';
import { useInAppGoogleGuard } from '../OpenInBrowserPrompt';

const API = process.env.REACT_APP_API_BASE_URL;

// No password to choose (as the main sign-up form): the account gets a
// random one nobody needs to know; they log in later by emailed link.
const randomPassword = () => {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return `${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}Aa1!`;
};

// Final step of the AI onboarding chat: "save your answers and find people" —
// create the account with Google, Facebook or just their email (no password,
// like the main sign-up form), then the page saves everything they told the
// bot to their profile. An email that already has an account gets a link to
// log in instead.
const AccountForm = ({ email: leadEmail, firstName, communityId, sessionId, onAuthenticated, showNoCommitment = false }) => {
  const [email, setEmail] = useState(leadEmail || '');
  // Their first name for the account — filled in if the chat picked it up.
  const [name, setName] = useState(firstName || '');
  // The ticks sit at the bottom: tapping Google / Facebook / the button before
  // ticking the terms points them there instead.
  const termsRef = useRef(null);
  const [needTerms, setNeedTerms] = useState(false);
  const termsFirst = (fn) => (...args) => {
    if (!terms) {
      setNeedTerms(true);
      termsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      termsRef.current?.querySelector('input')?.focus();
      return undefined;
    }
    return fn(...args);
  };
  // The terms (and, where the community offers it, "No commitment") —
  // ticked here now there's no separate email step before signing up.
  const [terms, setTerms] = useState(false);
  const [noCommitment, setNoCommitment] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const mode = 'signup';
  // Opened in their own browser, the chat carries on from here (?session=).
  const { guard, prompt: openInBrowserPrompt } = useInAppGoogleGuard(() => {
    const u = new URL(window.location.href);
    u.searchParams.delete('new');
    if (sessionId) u.searchParams.set('session', sessionId);
    return u.toString();
  }, mode === 'login' ? 'Log in with email instead' : 'Sign up with email instead');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { createNewUser } = useCreateNewUser();
  const { login } = useLogin();
  const auth = getAuth(firebaseApp);

  // Make sure the Interconnector account exists for this Firebase user, then
  // hand back to the page to save the chat answers and move on.
  const finish = async (user, fallbackName) => {
    const token = await getIdToken(user);
    const check = await fetch(`${API}/user/check/${user.uid}`, { headers: { Authorization: `Bearer ${token}` } });

    let isNewUser = false;
    if (check.status === 404) {
      // A Facebook account may have no email — use the one they gave the chat.
      const result = await createNewUser(user.email || leadEmail || '', fallbackName || firstName || '', communityId, user.uid, true);
      if (!result?.success) throw new Error('signup_failed');
      isNewUser = true;
    } else if (check.ok) {
      const ok = await login(user.uid);
      if (!ok) throw new Error('login_failed');
    } else {
      throw new Error('check_failed');
    }

    // Their first name (typed here, or from Google / Facebook) — so the
    // profile step afterwards doesn't ask again.
    await onAuthenticated({ user, isNewUser, firstName: fallbackName || firstName || '', consents: { terms, deleteIfInactive: noCommitment } });
  };

  const run = async (fn) => {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'auth/email-already-in-use') {
        // Already has an account: email them a link to log straight in (as
        // the main sign-up form does) — it brings them back to this chat.
        try {
          const back = new URL(window.location.href);
          if (sessionId) back.searchParams.set('session', sessionId);
          await sendSignInLinkToEmail(auth, email.trim(), { url: back.toString(), handleCodeInApp: true });
          try { window.localStorage.setItem('emailForSignIn', email.trim()); } catch { /* ignore */ }
          setLinkSent(true);
          setError('');
        } catch {
          setError('You already have an account with this email — please log in from the Log in page.');
        }
      } else if (code === 'auth/account-exists-with-different-credential') {
        setError('You already have an account with that email — continue with Google or your email instead.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential' || code === 'auth/user-not-found') {
        setError("That email and password don't match. Try again, or use Google.");
      } else if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        setError('');
      } else {
        console.error('[AIOnboarding] account error:', err);
        setError('Something went wrong setting up your account — please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => run(async () => {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    await finish(result.user, result.user.displayName?.split(' ')[0]);
  });

  const handleFacebook = () => run(async () => {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    const result = await signInWithPopup(auth, provider);
    await finish(result.user, result.user.displayName?.split(' ')[0]);
  });

  const handleEmail = (e) => {
    e.preventDefault();
    if (!terms) { termsFirst(() => {})(); return; }
    run(async () => {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), randomPassword());
      await finish(cred.user, name.trim());
    });
  };

  if (linkSent) {
    return (
      <div className="aio-form">
        <p className="demo-paragraph">
          You already have an account with <strong>{email.trim()}</strong>, so we&apos;ve emailed you a link to log in —
          it brings you straight back here to finish.
        </p>
      </div>
    );
  }

  return (
    <div className="aio-form">

      <button type="button" className="aio-google-btn" onClick={termsFirst(guard(handleGoogle))} disabled={busy}>
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
        </svg>
        Continue with Google
      </button>
      {openInBrowserPrompt}

      <button type="button" className="aio-facebook-btn" onClick={termsFirst(handleFacebook)} disabled={busy}>
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
        Continue with Facebook
      </button>

      <div className="aio-divider"><span>or just your email</span></div>

      <form onSubmit={handleEmail}>
        <label className="aio-label" htmlFor="aio-account-name">First name</label>
        <input
          id="aio-account-name"
          className="aio-input"
          type="text"
          autoComplete="given-name"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 50))}
          disabled={busy}
        />
        <label className="aio-label" htmlFor="aio-account-email">Email</label>
        <input
          id="aio-account-email"
          className="aio-input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
        <p className="aio-hint">No password needed — we&apos;ll email you a link whenever you need to log in.</p>

        {error && <div className="aio-error">{error}</div>}

        <label className="aio-check" ref={termsRef}>
          <input type="checkbox" checked={terms} onChange={(e) => { setTerms(e.target.checked); if (e.target.checked) setNeedTerms(false); }} disabled={busy} />
          <span>
            I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, I&apos;m 18 or over, and I&apos;m happy for you to email me suggested members.
          </span>
        </label>
        {showNoCommitment && (
          <label className="aio-check">
            <input type="checkbox" checked={noCommitment} onChange={(e) => setNoCommitment(e.target.checked)} disabled={busy} />
            <span><strong>No commitment (optional):</strong> delete my account after 3 months if I receive no suggestions I&apos;m interested in.</span>
          </label>
        )}
        {needTerms && !terms && <p className="aio-hint aio-hint--warn">Please tick the box above first.</p>}

        <button className="demo-signup-button" type="submit" disabled={busy || !name.trim() || !email}>
          {busy ? 'One moment…' : 'Save my answers & find people'}
        </button>
      </form>

      <a className="aio-link-btn" href="/login" target="_blank" rel="noopener noreferrer">Already have an account? Log in</a>
    </div>
  );
};

export default AccountForm;
